import { BulkOperationProps } from '@type/bulk-operations';
import { useTranslation } from 'next-i18next';
import { useSwrBulkContact } from '@hooks/useSwrApi/endpoints/bulk-operations';
import { Button } from '@components/common/Forms';
import { IdentificationIcon } from '@heroicons/react/20/solid';
import { i18nConfig } from 'i18n-config';
import { useOpenBulkModal } from './hooks/useOpenBulkModal';


/**
 * @description Show contact details in bulk for the job applicants list.
 *
 * - **getSelectedApplicants**: Get the selected applicants. Function that returns an array of selected applicants.
 * - **selectAll**: Boolean value that indicates if all applicants are selected.
 * - **status**: Status of the applicants.
 * - **jobSlug**: The job slug.
 * - **locale**: The locale.
 * - **queryParams**: The filters query params object.
 */
export const BulkContact = (props: BulkOperationProps) => {
  const {
    getSelectedApplicants,
    jobSlug = '',
    locale = i18nConfig.defaultLocale,
    selectAll,
    status,
    queryParams,
    normalizeSelectedApplicantsForBulkAction,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Handle the bulk applicants and base post object build
  const {
    getPostObject,
  } = useOpenBulkModal({
    getSelectedApplicants, selectAll, status, queryParams, normalizeSelectedApplicantsForBulkAction,
  });

  // SWR: Bulk contact applicants
  const {
    trigger,
    isMutating,
  } = useSwrBulkContact(jobSlug, locale);

  // Handle bulk contact
  const handleBulkContact = () => {
    const postObject = getPostObject();
    if (postObject) {
      void trigger(postObject);
    }
  };


  // Render component
  // *********************************
  return (
    <Button
      styling="text"
      size="sm"
      color="ink"
      className="grow px-4"
      aria-label={t('cv.action.bulk.contact')}
      isLoading={isMutating}
      onClick={handleBulkContact}
    >
      {!isMutating && <IdentificationIcon className="size-5 md:mr-1" />}
      <span className="hidden md:block lg:pl-px">{t('cv.action.bulk.contact')}</span>
    </Button>
  );
};
